/**
 * THE integration point. Every screen in the survey talks to this and to
 * nothing else.
 *
 * What it guarantees, in order of importance:
 *
 *  1. **Every write persists before it is visible.** `setAnswer` calls
 *     `SurveyStore.writeAnswer`, which commits to IndexedDB inside a
 *     transaction and appends the outbox row in the same transaction, and only
 *     then does React state move. There is no window in which an answer exists
 *     on screen but not on disk. A surveyor who force-quits mid-answer loses
 *     nothing.
 *
 *  2. **React state is a cache of the store, never the source of truth.** The
 *     provider subscribes to the store's change feed, so a document the sync
 *     engine adopts from the server (or a write from another tab) lands on
 *     screen without anybody wiring it through.
 *
 *  3. **The engines run in exactly one place.** `computeResults` (results.ts)
 *     is called here, on the document, after every change. A screen reads
 *     visibility, quantities, validation text and gaps out of the result — it
 *     never recomputes any of them.
 *
 *  4. **No screen builds an answer key.** `setAnswer(questionId, scope, value)`
 *     narrows the scope through `conditionScope` exactly the way the validation
 *     and calc engines do, so a lobby screen writing a building answer cannot
 *     invent "room:r1|grp:gA|B8" — a key that would never be read again.
 *
 * Out-of-order writes: two taps in flight at once can resolve in either order,
 * so an accepted record must never move the revision backwards. The guard is
 * `localRevision`, which the store bumps under the same transaction lock.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { applyAutoFix } from '../../core/validation';
import {
  isSubmittedStatus,
  type AnswerValue,
  type AutoFix,
  type DerivedLine,
  type MachineRoom,
  type Note,
  type QuestionDef,
  type Scope,
  type SectionInstance,
  type SurveyDocument,
  type ValidationIssue,
} from '../../core/types';
import type { DocumentMutate, StoredSurvey } from '../../db/store';
import { clearDefaultMark, type PendingDefault } from './answerDefaults';
import { clearAnswerWrite, defaultsWrite, surveyorAnswerWrite } from './surveyWrites';
// Imported from the file, not the barrel. The barrel now re-exports the
// connected question renderer, which imports this provider — going through it
// here would close an import cycle for the sake of two placeholder surfaces.
import { Card, Notice } from '../components/Surfaces';
import {
  computeResults,
  derivedFor as lookupDerived,
  issuesFor as lookupIssues,
  keyFor,
  visibleQuestions as listVisibleQuestions,
  type SurveyResults,
} from './results';
import { useServices } from './services';
import { guardedWrite } from './writeGate';

export interface SurveyContextValue {
  surveyId: string;
  /** The device's copy, including its sync bookkeeping. */
  record: StoredSurvey;
  doc: SurveyDocument;
  /** Everything the engines say about `doc`. Recomputed on every change. */
  results: SurveyResults;
  /** True while a write is in flight. Writes are already durable when it clears. */
  saving: boolean;
  /** Set when a write failed — a full disk, a closed database. */
  error: string | null;
  /**
   * `isSubmittedStatus(doc.status)` — round 7 task 10, controller ruling 12.
   *
   * Once the office has the survey, the surveyor cannot edit it: every door
   * this context exposes refuses (see `write()` below) once the document is
   * submitted, and every screen reads this to disable its own controls. It
   * lifts the moment the office recalls the survey — `store.adoptServerDocument`
   * lands an `in_progress` revision, and this flag is recomputed off it like
   * everything else here.
   */
  readOnly: boolean;

  /* ── writes: each one persists to IndexedDB before it resolves ───────── */
  setAnswer: (questionId: string, scope: Scope, value: AnswerValue) => Promise<void>;
  clearAnswer: (questionId: string, scope: Scope) => Promise<void>;
  /**
   * Structural edits — rooms, cars, groups, hoistways, ad hoc items, status.
   * Mutate the document in place; the store persists and re-queues it.
   */
  setStructure: (mutate: DocumentMutate) => Promise<void>;
  /** Apply a §4 rule 5 auto-fix. The payload comes from the validation engine. */
  applyFix: (fix: AutoFix) => Promise<void>;
  addNote: (note: Note) => Promise<void>;
  /**
   * Fill in the answers a set of on-screen questions opens on — D-016.
   *
   * One transaction for the whole batch, so opening a section costs one write
   * and one outbox row rather than one of each per question. The plan comes from
   * `pendingDefaults()`; the guards are re-checked inside the transaction, so a
   * stale plan can only write less, never more.
   */
  applyDefaults: (pending: readonly PendingDefault[]) => Promise<void>;

  /* ── reads: all delegate to the engines' output ──────────────────────── */
  answer: (questionId: string, scope?: Scope) => AnswerValue | undefined;
  derivedFor: (questionId: string, scope?: Scope) => readonly DerivedLine[];
  issuesFor: (questionId: string, scope?: Scope) => readonly ValidationIssue[];
  visibleQuestions: (section: SectionInstance) => QuestionDef[];
  sectionsForRoom: (roomId: string) => SectionInstance[];
  room: (roomId: string) => MachineRoom | undefined;
}

const SurveyContext = createContext<SurveyContextValue | null>(null);

/** A cheap change fingerprint — see the note in `accept` below. */
function signatureOf(record: StoredSurvey): string {
  return [
    record.localRevision,
    record.updatedAt,
    record.dirty,
    record.sealed,
    record.syncedRevision,
    // Photo upload state is written as bookkeeping and deliberately does NOT
    // bump the revision (store.ts, UpdateDocumentOptions.content), so it needs
    // its own place in the fingerprint or the section screen would never see a
    // photo finish uploading.
    record.document.photos.map((photo) => photo.uploadState).join(','),
  ].join('|');
}

export interface SurveyProviderProps {
  surveyId: string;
  children: ReactNode;
  /** Rendered while the document is being read out of IndexedDB. */
  fallback?: ReactNode;
  /** Rendered when this device holds no such survey. */
  missing?: ReactNode;
}

export function SurveyProvider({
  surveyId,
  children,
  fallback,
  missing,
}: SurveyProviderProps): ReactNode {
  const { store, log } = useServices();
  const [record, setRecord] = useState<StoredSurvey | null>(null);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'missing'>('loading');
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const revisionRef = useRef(-1);
  const signatureRef = useRef('');
  /**
   * The record `write()` checks against — a REF, not the `record` state, so a
   * refusal decision is never made off a stale closure. `setAnswer` and its
   * five siblings are rebuilt by the `value` useMemo whenever `record`
   * changes, which is normally enough on its own, but `write()` itself is a
   * stable `useCallback` shared by all of them; reading this ref inside it is
   * what lets ONE check in ONE place (ruling 12) stand for every door instead
   * of six copies of the same `isSubmittedStatus` test.
   */
  const recordRef = useRef<StoredSurvey | null>(null);

  /**
   * Accept a record read out of the store.
   *
   * Two filters. The revision guard drops a record that is OLDER than one
   * already applied — two writes in flight can resolve out of order, and
   * rendering the earlier one would put an answer back the way it was. The
   * signature guard drops a record that is IDENTICAL to the one on screen: the
   * store's change feed fires for every write to every survey, and re-rendering
   * the whole form (and re-running four engines) because a different survey
   * synced is work nobody asked for.
   */
  const accept = useCallback((next: StoredSurvey | undefined) => {
    if (!next) {
      recordRef.current = null;
      setRecord(null);
      setPhase('missing');
      return;
    }
    if (next.localRevision < revisionRef.current) return;
    const signature = signatureOf(next);
    if (signature === signatureRef.current && next.localRevision === revisionRef.current) {
      setPhase('ready');
      return;
    }
    revisionRef.current = next.localRevision;
    signatureRef.current = signature;
    recordRef.current = next;
    setRecord(next);
    setPhase('ready');
  }, []);

  useEffect(() => {
    revisionRef.current = -1;
    signatureRef.current = '';
    // Reset together with the other two (fix round 1, minor 7): left behind,
    // this would still hold the PREVIOUS survey's record — and its status —
    // until the new one is accepted, so a write against the new, still-
    // loading survey could be judged read-only or not off the wrong document.
    recordRef.current = null;
    setPhase('loading');
    let cancelled = false;

    const refresh = (): void => {
      void store
        .getSurvey(surveyId)
        .then((next) => {
          if (!cancelled) accept(next);
        })
        .catch((cause: unknown) => {
          if (!cancelled) setError(describe(cause));
        });
    };

    refresh();
    // The store fires this after every committed transaction — ours and the
    // sync engine's alike.
    const unsubscribe = store.subscribe(refresh);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [store, surveyId, accept]);

  const write = useCallback(
    async (run: () => Promise<StoredSurvey>): Promise<void> => {
      setPending((n) => n + 1);
      try {
        /*
         * THE ONE GATE — round 7 task 10, controller ruling 12. Every door
         * this context exposes (`setAnswer`, `clearAnswer`, `setStructure`,
         * `applyDefaults`, `applyFix`, `addNote`) calls `write`, so refusing
         * inside `guardedWrite` is refusing all six at once. Checked against
         * the CURRENT stored document (`recordRef`, not a closure captured
         * when the caller's handler was created) — a submission itself calls
         * `setStructure` while the document is still `in_progress`
         * (`applySubmission`), and that write must still land; it is only
         * writes AFTER the status flips that are refused.
         */
        const current = recordRef.current;
        const outcome = await guardedWrite(
          {
            readOnly: current !== null && isSubmittedStatus(current.document.status),
            surveyId,
            door: 'survey.write',
            log,
          },
          run,
        );
        // Refused: logged inside `guardedWrite`, nothing to accept. Not an
        // error — a refusal is not a failure a screen should surface, it is
        // a door that was never open (the caller already disabled or hid the
        // control that would have reached this).
        if (outcome === null) return;
        accept(outcome);
        setError(null);
      } catch (cause) {
        // The write did not land. Surfacing it is the whole response: retrying
        // silently would be guessing, and the surveyor needs to know the tap
        // did not take.
        setError(describe(cause));
        throw cause;
      } finally {
        setPending((n) => n - 1);
      }
    },
    [accept, surveyId, log],
  );

  const results = useMemo(
    () => (record ? computeResults(record.document) : null),
    [record],
  );

  const value = useMemo<SurveyContextValue | null>(() => {
    if (!record || !results) return null;
    return {
      surveyId,
      record,
      doc: record.document,
      results,
      saving: pending > 0,
      error,
      readOnly: isSubmittedStatus(record.document.status),

      /*
       * A surveyor write goes through `updateDocument` rather than
       * `writeAnswer` for one reason: it must also RETIRE the D-016 default mark
       * on that key, in the same transaction as the answer. Tapping "No" on a
       * question already showing No stores the same value and is nonetheless a
       * different document — the timing gate reads the mark, not the value, to
       * decide whether the surveyor has engaged with a disconnect split. Two
       * transactions would leave a window in which the answer is the surveyor's
       * and the provenance still says otherwise.
       */
      // D-055: nothing is written to a question this document no longer asks
      // — a typed child committed as its parent's No hides it. D-056: that
      // dropped write, a clear of nothing, and defaults that found nothing
      // left to fill are NO_CHANGE: no revision bump, no push (surveyWrites.ts).
      setAnswer: (questionId, scope, answerValue) =>
        write(() => store.updateDocument(surveyId, surveyorAnswerWrite(questionId, scope, answerValue))),
      clearAnswer: (questionId, scope) =>
        write(() => store.updateDocument(surveyId, clearAnswerWrite(questionId, scope))),
      setStructure: (mutate) => write(() => store.updateDocument(surveyId, mutate)),
      applyDefaults: (pending) => write(() => store.updateDocument(surveyId, defaultsWrite(pending))),
      applyFix: (fix) =>
        write(() =>
          store.updateDocument(surveyId, (document) => {
            // The engine owns the arithmetic of its own fix; this only persists
            // the result of it.
            const before = document.answers;
            const after = applyAutoFix(document, fix).answers;
            document.answers = after;
            // §4 rule 5 states counts the office will price, so every key it
            // touched stops being a default. Read off the diff rather than off
            // `fix.set`, because `applyAutoFix` owns the scope narrowing.
            for (const key of Object.keys(after)) {
              if (before[key] !== after[key]) clearDefaultMark(document, key);
            }
          }),
        ),
      addNote: (note) => write(() => store.addNote(surveyId, note)),

      answer: (questionId, scope) => record.document.answers[keyFor(questionId, scope ?? {})],
      derivedFor: (questionId, scope) => lookupDerived(results, questionId, scope ?? {}),
      issuesFor: (questionId, scope) => lookupIssues(results, questionId, scope ?? {}),
      visibleQuestions: (section) => listVisibleQuestions(record.document, section),
      sectionsForRoom: (roomId) =>
        results.sections.filter((section) => section.scope.roomId === roomId),
      room: (roomId) => record.document.rooms.find((candidate) => candidate.id === roomId),
    };
  }, [record, results, surveyId, pending, error, store, write]);

  if (phase === 'loading') {
    return (
      fallback ?? (
        <Card>
          <p>Opening the survey…</p>
        </Card>
      )
    );
  }

  if (phase === 'missing' || !value) {
    return (
      missing ?? (
        <Card>
          <p>This device doesn&rsquo;t have that survey.</p>
          <Notice>
            Assigned surveys arrive when you have signal. If you started this one in the field on
            another device, it lives there until it syncs.
          </Notice>
        </Card>
      )
    );
  }

  return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>;
}

/** Inside a `SurveyProvider`, the document and its results are always present. */
export function useSurvey(): SurveyContextValue {
  const value = useContext(SurveyContext);
  if (!value) throw new Error('useSurvey must be used inside <SurveyProvider>');
  return value;
}

function describe(cause: unknown): string {
  if (cause instanceof Error) return cause.message;
  return 'Could not save to this device.';
}
